import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function PendingAccess({ onLogout }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center max-w-sm px-4">
        <p className="text-2xl font-bold text-slate-800 mb-2">No pages assigned</p>
        <p className="text-slate-500 text-sm">Your account is active. Ask your admin to assign pages to your account.</p>
        <button
          onClick={onLogout}
          className="mt-6 px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 transition-colors"
        >
          Log out
        </button>
      </div>
    </div>
  );
}


export default function ProtectedRoute({ children, slug, adminOnly, orgAdminOnly }) {
  const { user, loading, logout, isOrgAdmin, isAdmin } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg animate-pulse">
            <span className="text-white font-bold text-lg">C</span>
          </div>
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-200 border-t-blue-600" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Org admin only pages (e.g. /org-panel)
  if (orgAdminOnly && !isOrgAdmin && !user.is_superadmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admin only pages (org_admin or restaurant_admin)
  if (adminOnly && !isAdmin && !user.is_superadmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Staff module permission check
  if (slug && user.role === 'staff') {
    const pages = user.allowed_pages;
    const modulePerms = user.module_permissions || {};

    // Check module permissions first (new system)
    const moduleMap = {
      'dashboard': 'dashboard',
      'carlo-agent': 'carlo_agent',
      'events': 'events',
      'sales': 'sales',
      'finance-performance': 'finance',
      'products': 'products',
      'stock': 'inventory',
      'employees': 'employees',
      'tasks': 'tasks',
    };
    const moduleKey = moduleMap[slug];
    if (moduleKey) {
      const level = modulePerms[moduleKey] || 'none';
      if (level !== 'none') return children;
    }

    // Fallback to allowed_pages (legacy)
    const hasAnyAccess = pages && (
      Array.isArray(pages) ? pages.length > 0 : Object.keys(pages).length > 0
    );
    const isAllowed = pages && (
      Array.isArray(pages) ? pages.includes(slug) : slug in pages
    );
    if (!hasAnyAccess && !Object.values(modulePerms).some(v => v !== 'none')) {
      return <PendingAccess onLogout={() => { logout(); navigate('/login'); }} />;
    }
    if (!isAllowed && !(moduleKey && (modulePerms[moduleKey] || 'none') !== 'none')) {
      // Find first allowed page
      const firstAllowed = Array.isArray(pages) ? pages[0] : Object.keys(pages)[0];
      const firstModule = Object.entries(modulePerms).find(([, v]) => v !== 'none');
      if (firstAllowed) {
        return <Navigate to={`/${firstAllowed}`} replace />;
      } else if (firstModule) {
        const reverseMap = Object.fromEntries(Object.entries(moduleMap).map(([k, v]) => [v, k]));
        const path = reverseMap[firstModule[0]];
        if (path) return <Navigate to={`/${path}`} replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}
